<script setup lang="ts" generic="T extends ContractSuggestionData">

import { ref, computed } from 'vue';
import type { HandlungBausteinProps, ContractSuggestionData } from '@/api/handlungen/types';
import { DsAlert, DsButton } from '@demvsystems/design-components';
import CreateContractModal from '@/contracts/components/CreateContractModal.vue';
import DetailValue from '@/contracts/components/DetailValue.vue';
import {
  formatDecimal,
  formatCentsToEuro
} from '@/application/utils/money.ts';
import { formatDate } from '@/application/utils/date';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import { useGesellschaften } from '@/application/composables/gesellschaften.ts';
import { toOptions } from '@/application/composables/vselect.ts';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';
import { closeHandlung } from '@/api/handlungen/handlungenApi.ts';
import { useRouter } from 'vue-router';
import { Finanzmanager } from '@/application/router/types/types.ts';
import { useHandlungStore } from '@/handlungen/store/handlungStore.ts';
import { useSparten } from '@/documents/composables/sparten.ts';

const { baustein, handlungId } = defineProps<HandlungBausteinProps<T>>();

const showCreateModal = ref(false);

const router = useRouter();
const { duzen } = useBaseDataStore();
const { gesellschaften, fetchGesellschaften } = useGesellschaften();
const { sparten, fetchSparten } = useSparten();
const { fetchHandlungenSilently } = useHandlungStore();

const companyOptions = toOptions(gesellschaften, 'name', 'id');
const spartenOptions = toOptions(sparten, 'display_name', 'id');

function handleContractSuggestion() {
  showCreateModal.value = true;
  useTagManager().push({ event: TrackEventCategory.CONTRACT_SUGGESTION, action: 'check' })
}

const initValues = {
  ...baustein.data,
  paymentAmount: formatCentsToEuro(baustein.data.paymentAmount),
};

const contractDetails = computed(() => [
  { label: 'Vertragsnummer', value: baustein.data.contractNumber },
  { label: 'Gesellschaft', value: companyOptions.value.find(val => val.value === baustein.data.gesellschaftId)?.label ?? '-' },
  { label: 'Sparte', value: spartenOptions.value.find(val => val.value === baustein.data.sparteId)?.label ?? '-' },
  { label: 'Beitrag', value: baustein.data.paymentAmount > 0 ? formatDecimal(baustein.data.paymentAmount / 100) : '-' },
  { label: 'Zahlweise', value: baustein.data.paymentInterval },
  { label: 'Beginn', value: baustein.data.startDate !== null ? formatDate(baustein.data.startDate) : '-' },
])

async function handleClose() {
  await closeHandlung({ handlungId });
  useTagManager().push({ event: TrackEventCategory.CONTRACT_SUGGESTION, action: 'reject' });
  await fetchHandlungenSilently();
  await router.push({ name: Finanzmanager.HANDLUNGEN });
}

async function onCreated() {
  await closeHandlung({ handlungId });
  await fetchHandlungenSilently();
  await router.push({ name: Finanzmanager.HANDLUNGEN });
}

fetchGesellschaften();
fetchSparten();
</script>

<template>
  <div class="flex flex-col h-full">
    <DsAlert type="primary">
      Diese Vertragsinformationen wurde über die Banking Verknüpfung gefunden und konnten keinem Vertrag im Finanzmanager
      zugeordnet werden. Jetzt Vertrag hinzufügen und {{ duzen('Ihr', 'dein') }} Makler wird direkt informiert.
      Daten stimmen nicht? Im nächsten Schritt können alle Angaben angepasst werden.
    </DsAlert>
    <div class="flex flex-col divide-y divide-gray-100 my-5">
      <template v-for="detail in contractDetails" :key="detail.label">
        <div class="flex flex-row items-start justify-between gap-2 py-3">
          <span class="font-normal text-gray-700 text-sm w-2/5 md:max-w-1/3 shrink-0">
            {{ detail.label }}
          </span>
          <DetailValue :value="detail.value" />
        </div>
      </template>
    </div>
    <div class="flex flex-col-reverse md:flex-row items-center justify-end gap-5">
      <DsButton
        size="lg"
        class="mt-auto w-full md:w-auto"
        variant="secondary"
        :handler="handleClose"
      >
        Vorschlag verwerfen
      </DsButton>
      <DsButton
        size="lg"
        class="mt-auto w-full md:w-auto"
        @click="handleContractSuggestion"
      >
        Vertrag hinzufügen
      </DsButton>
    </div>
    <CreateContractModal
      :show="showCreateModal"
      skip-ai-upload
      :contract-suggestion-data="initValues"
      base-height="88%"
      @created="onCreated"
      @close="showCreateModal = false"
    />
  </div>
</template>
